import React, { useState, useEffect, useMemo } from "react";
import { format } from "date-fns";
import { Calendar, User, Search, CheckCircle, X, Loader2, UserCheck, AlertCircle, Clock, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "@/hooks/use-toast";
import { scheduleAppointment, getDoctors, getClinics, getDoctor, getClinic } from "@/lib/api";

import { Doctor } from "@/types/doctor";
import { Clinic } from "@/types/clinic";
import { useAuth } from "@/context/AuthContext";

interface Patient {
  id: string;
  name: string;
  age: number;
  gender: string;
  phone: string;
  email?: string;
}

interface AddAppointmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  patientId?: string;
  patients?: Patient[];
  onAdded?: () => void;
  onPatientSearch?: () => void;
  showPatientSelection?: boolean;
  title?: string;
  description?: string;
}

interface AppointmentForm {
  patient_id: string;
  doctor_id: string;
  clinic_id: string;
  date: string;
  time: string;
  reason: string;
  status?: string;
}

export default function AddAppointmentModal({
  isOpen,
  onClose,
  patientId = '',
  patients = [],
  onAdded,
  onPatientSearch,
  showPatientSelection = true,
  title = "New Appointment",
  description = "Schedule a new patient appointment"
}: Readonly<AddAppointmentModalProps>) {
  const { currentUser } = useAuth();

  const [form, setForm] = useState<AppointmentForm>({
    patient_id: patientId,
    doctor_id: '',
    clinic_id: '',
    date: format(new Date(), "yyyy-MM-dd"),
    time: null,
    reason: null
  });

  const [loading, setLoading] = useState(false);
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [loadingData, setLoadingData] = useState(false);

  const isDoctor = currentUser?.role?.toLowerCase() === "doctor" && currentUser?.doctor_id && currentUser?.clinic_id;
  const isStaff = !isDoctor && !!currentUser?.clinic_id;

  const doctorsInClinic = useMemo(
    () => (form.clinic_id ? doctors.filter((d) => d.clinic_id === form.clinic_id) : doctors),
    [doctors, form.clinic_id]
  );
  const doctorDropdownDisabled = isStaff && doctorsInClinic.length === 1;

  useEffect(() => {
    if (patientId !== form.patient_id) {
      setForm(prev => ({ ...prev, patient_id: patientId }));
    }
  }, [patientId]);

  useEffect(() => {
    if (!isOpen) {
      setForm({
        patient_id: '',
        doctor_id: isDoctor ? currentUser?.doctor_id || '' : '',
        clinic_id: (isDoctor || isStaff) ? currentUser?.clinic_id || '' : '',
        date: format(new Date(), "yyyy-MM-dd"),
        time: "",
        reason: ""
      });
      setLoading(false);
      setLoadingData(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setForm(prev => ({
        ...prev,
        patient_id: patientId || '',
        doctor_id: isDoctor ? currentUser?.doctor_id || '' : prev.doctor_id,
        clinic_id: (isDoctor || isStaff) ? currentUser?.clinic_id || '' : prev.clinic_id,
        date: format(new Date(), "yyyy-MM-dd"),
        time: "",
        reason: ""
      }));
    }
  }, [isOpen, patientId]);

  useEffect(() => {
    if (!isOpen) return;
    if (isDoctor && currentUser?.doctor_id && currentUser?.clinic_id) {
      loadDoctorAndClinicForDoctor();
    } else if (isStaff && currentUser?.clinic_id) {
      loadDoctorsAndClinicForStaff();
    }
  }, [isOpen, isDoctor, isStaff, currentUser?.doctor_id, currentUser?.clinic_id]);

  const loadDoctorAndClinicForDoctor = async () => {
    if (!currentUser?.doctor_id || !currentUser?.clinic_id) return;
    setLoadingData(true);
    try {
      const [doctorData, clinicData] = await Promise.all([
        getDoctor(currentUser.doctor_id),
        getClinic(currentUser.clinic_id)
      ]);
      setDoctors(doctorData ? [doctorData] : []);
      setForm(prev => ({
        ...prev,
        doctor_id: currentUser.doctor_id,
        clinic_id: currentUser.clinic_id
      }));
    } catch (err: any) {
      toast({ title: "Loading Error", description: "Failed to load your clinic info", variant: "destructive" });
    } finally {
      setLoadingData(false);
    }
  };

  const loadDoctorsAndClinicForStaff = async () => {
    const clinicId = currentUser?.clinic_id;
    if (!clinicId) return;
    setLoadingData(true);
    try {
      const [doctorsData, clinicsData] = await Promise.all([getDoctors(), getClinics()]);
      const allDoctors = doctorsData || [];
      const doctorsAtClinic = allDoctors.filter((d: Doctor) => d.clinic_id === clinicId);
      setDoctors(doctorsAtClinic);
      setForm(prev => {
        const next = { ...prev, clinic_id: clinicId };
        if (doctorsAtClinic.length > 0 && !prev.doctor_id) {
          next.doctor_id = doctorsAtClinic[0].id;
        }
        return next;
      });
    } catch (err: any) {
      toast({ title: "Loading Error", description: "Failed to load doctors and clinic", variant: "destructive" });
    } finally {
      setLoadingData(false);
    }
  };

  const selectedPatient = useMemo(() => {
    if (!form.patient_id) return null;
    return patients.find(p => p.id === form.patient_id) || null;
  }, [patients, form.patient_id]);

  const handleInputChange = (field: keyof AppointmentForm) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleTimeChange = (time: string) => {
    setForm(prev => ({ ...prev, time }));
  };

  const handlePatientDeselect = () => {
    setForm(prev => ({ ...prev, patient_id: '' }));
    setTimeout(() => { onPatientSearch?.(); }, 0);
  };

  const formValidationErrors = useMemo(() => {
    const errors: string[] = [];
    if (showPatientSelection && !form.patient_id) errors.push('Patient required');
    if (showPatientSelection && form.patient_id && !selectedPatient) errors.push('Patient not found');
    if (!form.doctor_id) errors.push('Doctor required');
    if (!form.clinic_id) errors.push('Clinic required');
    if (!form.date) errors.push('Date required');
    if (!form.time) errors.push('Time required');
    return errors;
  }, [showPatientSelection, form, selectedPatient]);

  const isFormValid = formValidationErrors.length === 0;

  const handleSubmit = async () => {
    if (!isFormValid) {
      toast({ title: "Validation Error", description: "Please fill in all required fields", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      await scheduleAppointment({
        patient_id: form.patient_id,
        doctor_id: form.doctor_id,
        clinic_id: form.clinic_id,
        date: form.date,
        time: form.time,
        reason: form.reason.trim(),
        status: 'scheduled'
      });
      toast({ title: "Appointment Scheduled", description: "Appointment successfully scheduled" });
      onAdded?.();
    } catch (err: any) {
      toast({ title: "Scheduling Failed", description: err.message || "Failed to schedule", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader className="bg-gradient-to-r from-primary/5 to-primary/10 -m-6 mb-6 p-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-primary/10 rounded-full flex items-center justify-center">
              <Calendar className="w-4 h-4 text-primary" />
            </div>
            <div>
              <DialogTitle className="text-base font-semibold text-foreground">{title}</DialogTitle>
              <DialogDescription className="text-xs text-muted-foreground">{description}</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {loadingData ? (
          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <Loader2 className="w-7 h-7 text-primary animate-spin mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">Loading...</p>
            </div>
          </div>
        ) : (
          <>
            {/* Selected patient card */}
            {showPatientSelection && selectedPatient && (
              <div className="flex items-center justify-between p-3 bg-primary/5 border border-primary/20 rounded-lg mb-2">
                <div className="flex items-center gap-2 min-w-0">
                  <CheckCircle className="w-4 h-4 text-primary flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground capitalize truncate">{selectedPatient.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {selectedPatient.age}y • {selectedPatient.gender} • {selectedPatient.phone}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handlePatientDeselect}
                  disabled={loading}
                  className="p-1 rounded hover:bg-muted text-muted-foreground ml-2 flex-shrink-0"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            )}

            <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }} className="space-y-4">

              {/* Patient selection */}
              {showPatientSelection && !selectedPatient && (
                <div>
                  <label className="block text-xs font-medium text-foreground mb-1.5">
                    Patient *
                  </label>
                  {form.patient_id && !selectedPatient ? (
                    <div className="p-3 bg-destructive/5 border border-destructive/20 rounded-lg">
                      <p className="text-sm text-destructive font-medium">Patient not found</p>
                      <button type="button" onClick={handlePatientDeselect} className="text-xs text-destructive hover:underline mt-1">
                        Clear selection
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={onPatientSearch}
                      disabled={loading}
                      className="w-full px-4 py-2.5 border-2 border-dashed border-border text-muted-foreground hover:border-primary hover:text-primary rounded-lg transition-colors flex items-center gap-2 justify-center text-sm"
                    >
                      <Search className="w-4 h-4" />
                      Search & Select Patient
                    </button>
                  )}
                </div>
              )}

              {/* Doctor — only show for staff */}
              {!isDoctor && (
                <div>
                  <label className="block text-xs font-medium text-foreground mb-1.5">
                    Doctor *
                  </label>
                  <select
                    value={form.doctor_id}
                    onChange={handleInputChange('doctor_id')}
                    disabled={Boolean(loading || loadingData || doctorDropdownDisabled)}
                    className="w-full px-3 py-2 border border-border rounded-lg bg-background text-sm text-foreground focus:outline-none focus:border-ring disabled:opacity-70"
                    required
                  >
                    <option value="">Select a doctor</option>
                    {(doctorsInClinic.length ? doctorsInClinic : doctors).map(doctor => (
                      <option key={doctor.id} value={doctor.id}>
                        {doctor.name}{doctor.specialty ? ` — ${doctor.specialty}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Date and Time */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-foreground mb-1.5">
                    Date *
                  </label>
                  <Input
                    type="date"
                    value={form.date}
                    onChange={handleInputChange('date')}
                    disabled={loading}
                    className="h-9 text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground mb-1.5">
                    Time *
                  </label>
                  <input
                    type="time"
                    value={form.time}
                    onChange={e => handleTimeChange(e.target.value)}
                    disabled={loading}
                    className="h-9 w-full px-3 py-1 text-sm border border-input rounded-lg bg-background text-foreground focus:outline-none focus:border-ring disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>
              </div>

              {/* Reason */}
              <div>
                <label className="block text-xs font-medium text-foreground mb-1.5">
                  Reason <span className="text-muted-foreground font-normal">(optional)</span>
                </label>
                <Textarea
                  value={form.reason}
                  onChange={handleInputChange('reason')}
                  placeholder="e.g. Follow-up, Routine checkup, Fever..."
                  disabled={loading}
                  rows={2}
                  className="resize-none text-sm"
                />
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
                <Button type="button" variant="ghost" size="sm" onClick={onClose} disabled={loading}>
                  Cancel
                </Button>
                <Button type="submit" size="sm" disabled={loading || !isFormValid || loadingData} className="gap-2">
                  {loading ? (
                    <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Scheduling...</>
                  ) : (
                    <><Calendar className="w-3.5 h-3.5" /> Schedule Appointment</>
                  )}
                </Button>
              </div>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}